import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const SECTION_FALLBACKS = {
  response: 'No response body',
  record: 'Record data unavailable.',
  payload: 'Payload data unavailable.',
};

const SendHistoryResponseModal = ({ open, onClose, entry, record }) => {
  const [fullscreenViewer, setFullscreenViewer] = useState(null);
  const jsonViewStyle = useMemo(() => ({ backgroundColor: 'transparent', color: 'inherit' }), []);
  const response = entry?.responseBody || entry?.errorMessage || 'No response body';
  const payload = useMemo(
    () => (entry && Array.isArray(entry.payload) ? entry.payload : []),
    [entry]
  );

  const jsonSections = useMemo(
    () => ({
      response,
      record,
      payload,
    }),
    [response, record, payload]
  );

  const renderJsonValue = (value, fallbackMessage) => {
    if (value && typeof value === 'object') {
      return (
        <JsonView
          src={value}
          collapsed={2}
          theme="winter-is-coming"
          style={jsonViewStyle}
          displayDataTypes={false}
          displayObjectSize={false}
        />
      );
    }
    if (value == null || value === '') {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
          {fallbackMessage}
        </Typography>
      );
    }
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {String(value)}
      </Typography>
    );
  };

  if (!entry) return null;

  const handleCloseFullscreen = () => setFullscreenViewer(null);

  const renderSectionHeader = (title, key) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Tooltip title="Fullscreen">
        <IconButton size="small" onClick={() => setFullscreenViewer(key)}>
          <FullscreenIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const overlayFallback = fullscreenViewer ? SECTION_FALLBACKS[fullscreenViewer] : null;
  const overlayValue = fullscreenViewer ? jsonSections[fullscreenViewer] : null;
  const overlayContent =
    fullscreenViewer && overlayFallback ? (
      <Box
        sx={(theme) => ({
          position: 'absolute',
          inset: 0,
          bgcolor: theme.palette.background.paper,
          zIndex: theme.zIndex.modal + 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2,
        })}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={handleCloseFullscreen}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>{renderJsonValue(overlayValue, overlayFallback)}</Box>
      </Box>
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          height: '80vh',
          resize: 'both',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle>API Response Details</DialogTitle>
      <DialogContent dividers sx={{ position: 'relative', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`Status: ${entry.status || 'unknown'}`} color={entry.status === 'success' ? 'success' : 'warning'} />
          {entry.statusCode && <Chip label={`HTTP ${entry.statusCode}`} />}
          {entry.durationMs != null && <Chip label={`Duration: ${entry.durationMs} ms`} />}
          <Chip label={`Sent: ${formatDateTime(entry.createdAt)}`} />
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', pt: 1 }}>
          <Box>
            {renderSectionHeader('API Response', 'response')}
            <Box sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 1, backgroundColor: 'background.paper' }}>
              {renderJsonValue(jsonSections.response, SECTION_FALLBACKS.response)}
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            {renderSectionHeader('Submitted Record', 'record')}
            <Box sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 1, backgroundColor: 'background.paper' }}>
              {renderJsonValue(jsonSections.record, SECTION_FALLBACKS.record)}
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            {renderSectionHeader(`Full Payload (${payload.length} record${payload.length === 1 ? '' : 's'})`, 'payload')}
            <Box sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 1, backgroundColor: 'background.paper' }}>
              {renderJsonValue(jsonSections.payload, SECTION_FALLBACKS.payload)}
            </Box>
          </Box>
        </Box>
        {overlayContent}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendHistoryResponseModal;
